import { MutableRefObject, useEffect, useRef, useState } from "react";

import { CloseConfirmation } from "@/design-system/components/CloseConfirmation/CloseConfirmation";

import { ConfirmCallback, ConfirmFn, noopConfirm } from "./types";

interface PendingConfirmation {
  message: string;
  callback: ConfirmCallback;
}

interface UnsavedChangesHostProps {
  confirmRef: MutableRefObject<ConfirmFn>;
}

/**
 * Bridges react-router's imperative `getUserConfirmation` to a
 * React-rendered confirmation dialog.
 *
 * Why a ref instead of context:
 * `<BrowserRouter getUserConfirmation={fn}>` takes a function that the
 * router invokes from outside the React tree with a `(message, callback)`
 * pair. That function must be defined above the router, but the modal
 * that renders the prompt has to live inside it — so something must
 * bridge the two, with stable identity (handing BrowserRouter a fresh
 * function on every render would destabilize it).
 *
 * The ref satisfies both: App holds `confirmRef` and binds one stable
 * `getUserConfirmation` that just dereferences `confirmRef.current`.
 * This host mounts inside the router, registers its state-aware handler
 * onto `confirmRef.current`, and restores `noopConfirm` on unmount.
 *
 * Context cannot replace this directly because `useContext` is not
 * usable outside a component, so a consumer cannot supply the
 * BrowserRouter prop. A Provider could wrap the pattern, but it would
 * still need a ref underneath — driving the active handler through
 * `setState` would change the identity of the function passed to
 * BrowserRouter on every update.
 */
export function UnsavedChangesHost({ confirmRef }: UnsavedChangesHostProps) {
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  // The Dialog runs a close animation when `open` flips to false, during
  // which the previous description must remain visible. Tracking the
  // last shown message separately keeps the text stable through the
  // exit animation instead of flashing back to the default.
  const [lastMessage, setLastMessage] = useState<string | undefined>(undefined);
  // Mirror the latest pending state in a ref so the install effect can read it
  // without re-running. The effect must run exactly once per mount so the
  // confirmRef bridge is stable for the lifetime of the host.
  const pendingRef = useRef<PendingConfirmation | null>(null);
  pendingRef.current = pending;

  useEffect(() => {
    confirmRef.current = (message, callback) => {
      const previous = pendingRef.current;
      if (previous) {
        previous.callback(false);
      }
      setPending({ message, callback });
      setLastMessage(message);
    };
    return () => {
      confirmRef.current = noopConfirm;
    };
  }, [confirmRef]);

  function resolve(allow: boolean) {
    const current = pendingRef.current;
    if (!current) {
      return;
    }
    current.callback(allow);
    setPending(null);
  }

  return (
    <CloseConfirmation
      open={pending !== null}
      description={lastMessage}
      onConfirm={() => resolve(true)}
      onCancel={() => resolve(false)}
    />
  );
}
